// Schemas for routes-advisor-calls.ts (browser-safe zod). Operating basics for advisor calling on Telnyx (plan B7):
// call history («Mis llamadas» + the manager view), team presence and simple per-advisor / per-line reports.
//
// Honesty rules encoded here:
//  • only advisor calls (softphone calls on Telnyx lines and unanswered calls of «advisors» lines) are listed or
//    counted — AI calls, admin test calls and copilot simulations never are; calls with test contacts are not counted;
//  • unknown stays unknown: an average without samples is null (never 0), a handle time without both ends is null;
//  • a recording is shown as available only when stored by LIA, and listenable only with the recordings permission.
import { z } from 'zod';
import { CallStatusSchema } from './enums.js';
import { CallRecordingStateSchema } from './api-recording.js';
import { SoftphonePresenceSchema } from './api-softphone.js';

const Iso = z.string();
const Id = z.string().min(1).max(64);
const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Usa el formato AAAA-MM-DD');

/** What happened with an advisor call, from the advisor's point of view. */
export const ADVISOR_CALL_OUTCOMES = ['answered', 'missed', 'voicemail', 'no_answer', 'busy', 'failed', 'in_progress'] as const;
export const AdvisorCallOutcomeSchema = z.enum(ADVISOR_CALL_OUTCOMES);
export type AdvisorCallOutcome = z.infer<typeof AdvisorCallOutcomeSchema>;

export const ADVISOR_CALL_OUTCOME_LABELS: Record<AdvisorCallOutcome, string> = {
  answered: 'Contestada',
  missed: 'Perdida',
  voicemail: 'Mensaje de voz',
  no_answer: 'Sin respuesta',
  busy: 'Ocupado',
  failed: 'Fallida',
  in_progress: 'En curso',
};

export const ListAdvisorCallsQuerySchema = z.object({
  /** Managers only (advisors always get their own calls; another advisor's id → 403). */
  membershipId: Id.optional(),
  endpointId: Id.optional(),
  projectId: Id.optional(),
  from: Day.optional(),
  to: Day.optional(),
  outcome: AdvisorCallOutcomeSchema.optional(),
  direction: z.enum(['inbound', 'outbound']).optional(),
  cursor: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ListAdvisorCallsQuery = z.infer<typeof ListAdvisorCallsQuerySchema>;

export const AdvisorCallRowSchema = z.object({
  callId: Id,
  direction: z.enum(['inbound', 'outbound']),
  outcome: AdvisorCallOutcomeSchema,
  status: CallStatusSchema,
  contactId: Id,
  contactName: z.string().nullable(),
  contactPhone: z.string().nullable(),
  endpointId: Id.nullable(),
  lineName: z.string().nullable(),
  lineE164: z.string().nullable(),
  projectId: Id.nullable(),
  projectName: z.string().nullable(),
  /** Advisor who had (or placed) the call; null = nobody answered. */
  advisorMembershipId: Id.nullable(),
  advisorName: z.string().nullable(),
  createdAt: Iso,
  answeredAt: Iso.nullable(),
  endedAt: Iso.nullable(),
  /** Seconds from the advisor being connected to the end; null when unknown or not answered. */
  handleSeconds: z.number().int().nullable(),
  /** null = no recording decision for this call. */
  recording: z.object({ state: CallRecordingStateSchema, available: z.boolean(), canListen: z.boolean() }).nullable(),
  /** The caller left a voicemail. */
  voicemail: z.boolean(),
  copilotSessionId: Id.nullable(),
  /** Missed-call task linked to this call (plan B7: exactly one per unanswered call). */
  callbackTaskId: Id.nullable(),
});
export type AdvisorCallRow = z.infer<typeof AdvisorCallRowSchema>;

export const AdvisorCallListSchema = z.object({
  items: z.array(AdvisorCallRowSchema),
  nextCursor: z.string().nullable(),
  /** true for advisors: only their own calls (placed, answered, or that rang their softphone). */
  selfOnly: z.boolean(),
  /** Filter options (empty for advisors). */
  advisors: z.array(z.object({ membershipId: Id, name: z.string() })),
  lines: z.array(z.object({ endpointId: Id, displayName: z.string(), phoneE164: z.string().nullable() })),
});
export type AdvisorCallList = z.infer<typeof AdvisorCallListSchema>;

// ───────── Team presence ─────────

export const TeamPresenceSchema = z.object({
  generatedAt: Iso,
  items: z.array(
    z.object({
      membershipId: Id,
      name: z.string(),
      role: z.string(),
      /** An admin enabled a softphone seat for this member. */
      seatEnabled: z.boolean(),
      presence: SoftphonePresenceSchema,
      /** Last heartbeat of their softphone (null = never connected). */
      lastSeenAt: Iso.nullable(),
      call: z.object({ direction: z.enum(['inbound', 'outbound']), since: Iso, contactName: z.string().nullable() }).nullable(),
    }),
  ),
});
export type TeamPresence = z.infer<typeof TeamPresenceSchema>;

// ───────── Reports ─────────

export const AdvisorCallReportQuerySchema = z.object({
  from: Day.optional(),
  to: Day.optional(),
  projectId: Id.optional(),
  endpointId: Id.optional(),
});
export type AdvisorCallReportQuery = z.infer<typeof AdvisorCallReportQuerySchema>;

export const AdvisorCallMetricsSchema = z.object({
  inbound: z.number().int(),
  outbound: z.number().int(),
  answered: z.number().int(),
  missed: z.number().int(),
  voicemails: z.number().int(),
  inProgress: z.number().int(),
  /** null = no answered call with a known duration (unknown, not 0). */
  avgHandleSeconds: z.number().int().nullable(),
  handleSamples: z.number().int(),
});
export type AdvisorCallMetrics = z.infer<typeof AdvisorCallMetricsSchema>;

export const ADVISOR_CALL_METRIC_KEYS = ['inbound', 'outbound', 'answered', 'missed', 'voicemails', 'avgHandleSeconds'] as const;

export const AdvisorCallReportSchema = z.object({
  range: z.object({ from: Day, to: Day, timezone: z.string() }),
  definitions: z.record(z.string(), z.object({ label: z.string(), definition: z.string() })),
  /** membershipId null = calls nobody answered and that rang no advisor («Sin asesor»). */
  byAdvisor: z.array(AdvisorCallMetricsSchema.extend({ membershipId: Id.nullable(), name: z.string() })),
  byLine: z.array(AdvisorCallMetricsSchema.extend({ endpointId: Id, displayName: z.string(), phoneE164: z.string().nullable() })),
  totals: AdvisorCallMetricsSchema,
});
export type AdvisorCallReport = z.infer<typeof AdvisorCallReportSchema>;
